import React, { PropsWithChildren, useEffect, useState } from "react"
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    Stack,
    Switch,
    Typography,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { makeStyles } from "tss-react/mui"
import { FormCard } from "./FormCard"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/pro-solid-svg-icons"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"

const useStyles = makeStyles()((theme) => ({
    accordion: {
        border: `1px solid ${theme.palette.tailwind.slate[100]}`,
        borderRadius: ".75rem !important",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
        "&::before": {
            display: "none",
        },
        "&.Mui-expanded": {
            margin: 0,
        },
        "& .MuiAccordionSummary-root": {
            minHeight: "auto",
            "&.Mui-expanded": {
                minHeight: "auto",
            },
        },
        "& .MuiAccordionSummary-content": {
            margin: "12px 0",
            "&.Mui-expanded": {
                margin: "12px 0",
            },
        },
    },
    accordionDetails: {
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
        padding: "1.75rem 2rem",
    },
    boxTitle: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[600],
    },
    defaultTitle: {
        marginBottom: "2rem",
    },
    groupTitle: {
        textTransform: "uppercase",
        padding: "1.5rem 2rem .5rem",
    },
    groupItem: {
        padding: "1rem 2rem",
    },
    accordionTitle: {
        paddingLeft: "1rem",
    },
    offContent: {
        textAlign: "center",
        color: theme.palette.tailwind.slate[400],
        fontStyle: "italic",
    },
    switchableHeader: {
        cursor: "pointer",
        padding: "7px .5rem 7px 2rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    switchableHeaderOpen: {
        padding: "1rem .5rem 1rem 2rem",
    },
    switchableBody: {
        borderTop: "1px solid rgb(226, 232, 240)",
        padding: "1.75rem 2rem",
    },
    createButton: {
        margin: ".3rem .5rem .3rem 0",
        textTransform: "none",
        borderRadius: "0.5rem",
    },
}))

type BodyProps = Readonly<PropsWithChildren<{}>>

function Body({ children }: BodyProps) {
    return (
        <Stack direction="column" spacing="1.25rem">
            {children}
        </Stack>
    )
}

type OffContentProps = Readonly<{
    offContent: string | React.ReactNode
    typographyClassName: string
}>

function OffContent({ offContent, typographyClassName }: OffContentProps) {
    return (
        <Box>
            {typeof offContent === "string" ? (
                <Typography className={typographyClassName}>{offContent}</Typography>
            ) : (
                offContent
            )}
        </Box>
    )
}

type StandardSidePanelProps = Readonly<
    PropsWithChildren<{
        variant?: "default" | "collapsible" | "switchable" | "switchableAndCollapsible" | "button" | "group"
        title: string
        expanded?: boolean
        checked?: boolean
        onCheckChange?: (checked: boolean) => void
        offContent?: string | React.ReactNode
        expandedDependsOn?: boolean
        onCreateButtonClick?: () => void
    }>
>

export function StandardSidePanel({
    children,
    title,
    variant = "default",
    expanded = true,
    checked = false,
    onCheckChange,
    offContent,
    expandedDependsOn = true, // USED FOR variant: "switchableAndCollapsible"
    onCreateButtonClick,
}: StandardSidePanelProps) {
    const { classes, cx } = useStyles()
    const [isChecked, setIsChecked] = useState(checked)
    const [expandedDependsOnParents, setExpandedDependsOnParents] = useState(expandedDependsOn)

    useEffect(() => {
        setExpandedDependsOnParents(expandedDependsOn)
    }, [expandedDependsOn])

    useEffect(() => {
        setIsChecked(checked)
    }, [checked])

    const handleToggleSwitchable = () => {
        const newState = !isChecked
        setIsChecked(newState)
        onCheckChange?.(newState)
    }

    if (variant === "default") {
        return (
            <FormCard>
                <Typography variant="subtitle1" className={cx(classes.boxTitle, classes.defaultTitle)}>
                    {title}
                </Typography>
                <Body>{children}</Body>
            </FormCard>
        )
    }

    if (variant === "group") {
        return (
            <FormCard isPadding={false}>
                <Typography variant="subtitle1" className={cx(classes.boxTitle, classes.groupTitle)}>
                    {title}
                </Typography>
                <Stack divider={<Divider />}>
                    {React.Children.toArray(children).map((child, index) => (
                        <Box key={index} className={classes.groupItem}>
                            {child}
                        </Box>
                    ))}
                </Stack>
            </FormCard>
        )
    }

    if (variant === "switchable") {
        const open = isChecked || offContent !== undefined
        return (
            <FormCard isPadding={false}>
                <Box
                    onClick={handleToggleSwitchable}
                    className={cx(classes.switchableHeader, { [classes.switchableHeaderOpen]: open })}
                >
                    <Typography variant="subtitle1" className={classes.boxTitle}>
                        {title}
                    </Typography>
                    <Switch checked={isChecked} onChange={handleToggleSwitchable} />
                </Box>
                {open && (
                    <Box className={cx(classes.switchableBody)}>
                        {isChecked && <Body>{children}</Body>}
                        {!isChecked && offContent && (
                            <OffContent offContent={offContent} typographyClassName={classes.offContent} />
                        )}
                    </Box>
                )}
            </FormCard>
        )
    }

    if (variant === "switchableAndCollapsible") {
        return (
            <Accordion
                defaultExpanded={expanded}
                expanded={expandedDependsOnParents}
                onChange={() => setExpandedDependsOnParents(!expandedDependsOnParents)}
                elevation={0}
                disableGutters
                className={classes.accordion}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box className={classes.switchableHeader}>
                        <Typography variant="subtitle1" className={classes.boxTitle}>
                            {title}
                        </Typography>
                        <Switch
                            checked={isChecked}
                            onChange={(_, state) => {
                                setIsChecked(state)
                                onCheckChange?.(state)
                            }}
                            onClick={(event) => {
                                event.stopPropagation()
                            }}
                        />
                    </Box>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                    {isChecked && <Body>{children}</Body>}
                    {!isChecked && offContent && (
                        <OffContent offContent={offContent} typographyClassName={classes.offContent} />
                    )}
                </AccordionDetails>
            </Accordion>
        )
    }

    if (variant === "button") {
        return (
            <FormCard isPadding={false}>
                <Box className={cx(classes.switchableHeader, { [classes.switchableHeaderOpen]: expanded })}>
                    <Typography variant="subtitle1" className={classes.boxTitle}>
                        {title}
                    </Typography>
                    {!expanded && (
                        <LocalizedStrict id="product-create-offer-create-button">
                            <Button
                                onClick={onCreateButtonClick}
                                color="primary"
                                variant="outlined"
                                startIcon={<FontAwesomeIcon icon={faPlus} />}
                                size="small"
                                className={classes.createButton}
                            >
                                Create
                            </Button>
                        </LocalizedStrict>
                    )}
                </Box>
                {expanded ? (
                    <Box className={cx(classes.switchableBody)}>
                        <Body>{children}</Body>
                    </Box>
                ) : (
                    <>{children} </>
                )}
            </FormCard>
        )
    }

    return (
        <Accordion defaultExpanded={expanded} elevation={0} disableGutters className={classes.accordion}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" className={cx(classes.boxTitle, classes.accordionTitle)}>
                    {title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
                <Body>{children}</Body>
            </AccordionDetails>
        </Accordion>
    )
}
